/**
 * One row per stand per date/shift group, as returned by
 * [MILL].[GetInformationRetrievalDataWeb].
 *
 * `Date` and `Shift` come back blank when the corresponding grouping toggle is
 * off — the procedure returns `1900-01-01` / `''` rather than omitting them.
 */
export interface InformationRetrievalRow {
	Stand: string;
	Date: string;
	Shift: string;
	Active: number | null;
	RFactorAvg: number | null;
	RFactorSum: number | null;
	RFactorCount: number | null;
	RFactorTargetAvg: number | null;
	RFactorTargetSum: number | null;
	RFactorMinAvg: number | null;
	RFactorMinSum: number | null;
	RFactorMaxAvg: number | null;
	RFactorMaxSum: number | null;
	RFactorRecipeMinAvg: number | null;
	RFactorRecipeMinSum: number | null;
	RFactorRecipeMaxAvg: number | null;
	RFactorRecipeMaxSum: number | null;
	/** Billets carrying a recipe target. */
	RecipeCount?: number | null;
	BilletsCount: number | null;
	/** Blank whenever the group spans more than one recipe. By design. */
	RecipeName: string;
}

export interface InformationRetrievalFilters {
	start: string;
	end: string;
	productTypeId: number | null;
	productSizeId: number | null;
	/** `'0'` = all shifts, `'-'` = current shift, otherwise the crew letter. */
	shiftId: string | null;
	groupByDate: boolean;
	groupByShift: boolean;
}

export interface ProductType {
	Id: number;
	Name: string;
}

export interface ProductSize {
	Id: number;
	Name: string;
	ProductTypeId: number;
}

export interface Crew {
	Id: string;
}

/** One crew / product type / product that has billets in the date range. */
export interface ProducedCombination {
	ProductTypeId: number | null;
	ProductSizeId: number | null;
	CrewId: string | null;
}

export interface InformationRetrievalPickers {
	crews: Crew[];
	productSizes: ProductSize[];
	productTypes: ProductType[];
	combinations: ProducedCombination[];
	/** The crew "Current shift" resolves to: the one on the latest billet. */
	currentCrewId: string | null;
}

export interface RecipeMeta {
	Id: number;
	Name: string;
	ProductSizeId: number;
	ProductSizeName: string;
	ProductTypeName: string;
}

export interface BuildRecipeParams {
	productSizeId: number;
	start: string;
	end: string;
	recipeId: number | null;
	name: string;
	limitsPercentage: number;
}

/** The four numbers stacked inside every cell of the per-group grid. */
export interface StandCell {
	avg: number | null;
	target: number | null;
	min: number | null;
	max: number | null;
	active: boolean;
}

/** One date/shift group as the grids and the chart draw it. */
export interface GroupRowBase {
	key: string;
	label: string;
	legendColor: string;
	recipeName: string;
	/** One cell per grid column: a mill stand or a straightener roll. */
	stands: Record<string, StandCell>;
}

/** One date/shift group: the unit the checkboxes select. */
export interface GroupRow extends GroupRowBase {
	/** Kept whole so the aggregate can re-read the Sum columns. */
	rows: InformationRetrievalRow[];
}

export type SummaryKey =
	| "actual"
	| "target"
	| "maxActual"
	| "minActual"
	| "maxRecipe"
	| "minRecipe"
	| "percOutOfLimits"
	| "stdWithRecipe";

export interface SummaryPropertyDef {
	key: SummaryKey;
	label: string;
	description: string;
	legendColor?: string;
	format: "FLOAT3" | "PERCENT" | "FLOAT2";
}

/**
 * The eight rows of the aggregate grid, in the legacy's display order, with its
 * legend colours. Names are the legacy's and are what the operators read.
 */
export const SUMMARY_PROPERTIES: readonly SummaryPropertyDef[] = [
	{
		key: "actual",
		label: "Avg Actual",
		description:
			"R-Factor the mill actually ran, across the selected date/shift groups",
		legendColor: "#244200",
		format: "FLOAT3",
	},
	{
		key: "target",
		label: "Recipe Actual",
		description: "Recipe target, averaged over every billet in the selection",
		legendColor: "#2A32FF",
		format: "FLOAT3",
	},
	{
		key: "maxActual",
		label: "Max Actual",
		description: "Highest R-Factor measured, averaged over the billets",
		legendColor: "#D800E6",
		format: "FLOAT3",
	},
	{
		key: "minActual",
		label: "Min Actual",
		description: "Lowest R-Factor measured, averaged over the billets",
		legendColor: "#D800E6",
		format: "FLOAT3",
	},
	{
		key: "maxRecipe",
		label: "Max Recipe",
		description: "Upper bound of the recipe tolerance band",
		legendColor: "#E60000",
		format: "FLOAT3",
	},
	{
		key: "minRecipe",
		label: "Min Recipe",
		description: "Lower bound of the recipe tolerance band",
		legendColor: "#E60000",
		format: "FLOAT3",
	},
	{
		key: "percOutOfLimits",
		label: "% out of limits",
		description: "Share of the selection that fell outside the measured band",
		format: "PERCENT",
	},
	{
		key: "stdWithRecipe",
		label: "STD with Recipe",
		description:
			"Standard deviation of the per-group average R-Factor for this stand",
		format: "FLOAT2",
	},
];

export type ChartDisplayMode = "average" | "straight";

/** Five green shades the legacy cycles through, one per selected group. */
export const GROUP_COLORS = [
	"#244200",
	"#4f7639",
	"#5DAA44",
	"#81D466",
	"#A5FF89",
];

export const CHART_COLORS = {
	target: "#2A32FF",
	actual: "#244200",
	band: "#D800E6",
	recipeBand: "#E60000",
} as const;
